import React, { useEffect, useMemo, useRef, useState } from "react";
import Layout from "../components/Layout";
import { Button } from "../components/ui/Button";
import calendarService from "../services/calendarService";
import { fmtDateOnly } from "../utils/datetime";
import { Calendar, Upload, Plus, Trash2, Clock, Info } from "lucide-react";

const TYPE_LABELS = {
  national: "Nacional", state: "Estadual", municipal: "Municipal",
  bridge: "Ponte", custom: "Customizado",
};
const TYPE_BADGE = {
  national: "bg-blue-100 text-blue-700", state: "bg-green-100 text-green-700",
  municipal: "bg-amber-100 text-amber-700", bridge: "bg-violet-100 text-violet-700",
  custom: "bg-gray-100 text-gray-600",
};
const minToTime = (m) => `${String(Math.floor((m || 0) / 60)).padStart(2, "0")}:${String((m || 0) % 60).padStart(2, "0")}`;
const timeToMin = (t) => {
  const [h, m] = String(t || "0:0").split(":").map((n) => parseInt(n, 10) || 0);
  return h * 60 + m;
};
const emptyHoliday = { date: "", name: "", type: "custom", region: "" };

const Calendario = () => {
  const [hours, setHours] = useState([]);
  const [savingHours, setSavingHours] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null); // {id?, date, name, type, region}
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const activeTenantName = useMemo(() => localStorage.getItem("active_tenant_name") || "", []);

  const flash = (msg) => { setMessage(msg); setError(""); };
  const fail = (e) => setError(e?.response?.data?.detail || "Ocorreu um erro.");

  const loadHolidays = async () => {
    setLoading(true);
    try {
      setHolidays(await calendarService.listHolidays(year, includeInactive));
    } catch (e) { fail(e); } finally { setLoading(false); }
  };

  useEffect(() => {
    calendarService.getBusinessHours().then(setHours).catch(fail);
  }, []);
  useEffect(() => { loadHolidays(); }, [year, includeInactive]);

  // ----- Expediente -----
  const setHour = (wd, field, value) =>
    setHours((prev) => prev.map((h) => (h.weekday === wd ? { ...h, [field]: value } : h)));

  const saveHours = async () => {
    setSavingHours(true);
    try {
      const payload = hours.map((h) => ({
        weekday: h.weekday,
        is_working: !!h.is_working,
        start_minute: Number(h.start_minute),
        end_minute: Number(h.end_minute),
      }));
      setHours(await calendarService.setBusinessHours(payload));
      flash("Expediente salvo. O SLA passa a contar em horário comercial.");
    } catch (e) { fail(e); } finally { setSavingHours(false); }
  };

  // ----- Feriados -----
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const res = await calendarService.uploadHolidays(file);
      flash(`Importação: +${res.inserted} novos, ~${res.updated} atualizados, ${res.skipped} ignorados.`);
      await loadHolidays();
    } catch (e2) { fail(e2); } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveHoliday = async () => {
    if (!editing?.date || !editing?.name?.trim()) { setError("Data e nome são obrigatórios."); return; }
    try {
      if (editing.id) await calendarService.updateHoliday(editing.id, editing);
      else await calendarService.createHoliday(editing);
      setEditing(null);
      flash("Feriado salvo.");
      await loadHolidays();
    } catch (e) { fail(e); }
  };

  const toggleActive = async (h) => {
    try { await calendarService.updateHoliday(h.id, { is_active: !h.is_active }); await loadHolidays(); }
    catch (e) { fail(e); }
  };
  const remove = async (h) => {
    if (!window.confirm(`Excluir o feriado "${h.name}" (${fmtDateOnly(h.date)})?`)) return;
    try { await calendarService.deleteHoliday(h.id); flash("Feriado excluído."); await loadHolidays(); }
    catch (e) { fail(e); }
  };

  const inputCls = "rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Calendário</h1>
          <p className="text-gray-500">
            Expediente e feriados usados no cálculo de SLA em horário comercial
            {activeTenantName ? ` — ${activeTenantName}` : ""}.
          </p>
        </div>

        {message && <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-green-700">{message}</div>}
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700">{error}</div>}

        {/* ===== EXPEDIENTE ===== */}
        <div className="rounded-lg border bg-white">
          <div className="flex items-center gap-2 border-b px-4 py-3">
            <Clock size={18} className="text-blue-600" />
            <h2 className="font-semibold text-gray-900">Horário de expediente</h2>
          </div>
          <div className="p-4 space-y-2">
            {hours.map((h) => (
              <div key={h.weekday} className="flex flex-wrap items-center gap-3 border-b last:border-0 py-2">
                <label className="flex items-center gap-2 w-44">
                  <input type="checkbox" className="h-4 w-4 accent-blue-600"
                    checked={!!h.is_working} onChange={(e) => setHour(h.weekday, "is_working", e.target.checked)} />
                  <span className="font-medium text-gray-800">{h.label}</span>
                </label>
                {h.is_working ? (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span>das</span>
                    <input type="time" className={inputCls}
                      value={minToTime(h.start_minute)}
                      onChange={(e) => setHour(h.weekday, "start_minute", timeToMin(e.target.value))} />
                    <span>às</span>
                    <input type="time" className={inputCls}
                      value={minToTime(h.end_minute)}
                      onChange={(e) => setHour(h.weekday, "end_minute", timeToMin(e.target.value))} />
                  </div>
                ) : (
                  <span className="text-sm text-gray-400">Sem expediente</span>
                )}
              </div>
            ))}
            <div className="flex justify-end pt-2">
              <Button type="button" onClick={saveHours} disabled={savingHours}>
                {savingHours ? "Salvando..." : "Salvar expediente"}
              </Button>
            </div>
          </div>
        </div>

        {/* ===== FERIADOS ===== */}
        <div className="rounded-lg border bg-white">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
            <div className="flex items-center gap-2">
              <Calendar size={18} className="text-blue-600" />
              <h2 className="font-semibold text-gray-900">Feriados e dias não úteis</h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select value={year} onChange={(e) => setYear(Number(e.target.value))} className={inputCls}>
                {[2024, 2025, 2026, 2027, 2028].map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <label className="flex items-center gap-1.5 text-xs text-gray-600">
                <input type="checkbox" className="h-4 w-4 accent-blue-600" checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)} /> Mostrar inativos
              </label>
              <Button type="button" variant="outline" onClick={() => setEditing({ ...emptyHoliday })}
                className="h-auto gap-1.5 px-3 py-2 text-gray-700">
                <Plus size={16} /> Novo
              </Button>
              <input ref={fileRef} type="file" accept=".xlsx,.pdf" className="hidden" onChange={handleUpload} />
              <Button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="h-auto gap-1.5 px-3 py-2">
                <Upload size={16} /> {uploading ? "Importando..." : "Importar (XLSX/PDF)"}
              </Button>
            </div>
          </div>

          <div className="px-4 py-3 text-xs text-gray-500 flex items-start gap-2 border-b">
            <Info size={14} className="mt-0.5 shrink-0" />
            <span>
              <strong>XLSX</strong>: colunas <span className="font-mono">data, nome, tipo (opcional), região (opcional)</span>.
              {" "}<strong>PDF</strong>: calendário anual com legendas por mês (ex.: "25 Dez (Sexta) – Natal") — o ano vem do nome do arquivo.
              Datas existentes são atualizadas; nomes acentuados do PDF podem precisar de ajuste manual.
            </span>
          </div>

          {loading ? (
            <div className="p-6 text-gray-500">Carregando...</div>
          ) : holidays.length === 0 ? (
            <div className="p-10 text-center text-gray-500">
              <Calendar className="mx-auto mb-2 text-gray-300" size={28} />
              Nenhum feriado em {year}.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[680px] text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-600">
                    <th className="px-4 py-3 font-semibold w-44">Data</th>
                    <th className="px-4 py-3 font-semibold">Nome</th>
                    <th className="px-4 py-3 font-semibold w-28">Tipo</th>
                    <th className="px-4 py-3 font-semibold w-28">Região</th>
                    <th className="px-4 py-3 font-semibold w-44 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {holidays.map((h) => (
                    <tr key={h.id} className={`border-b last:border-0 hover:bg-gray-50 ${h.is_active ? "" : "opacity-60"}`}>
                      <td className="px-4 py-3 text-gray-700">{fmtDateOnly(h.date)}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{h.name}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${TYPE_BADGE[h.type] || "bg-gray-100 text-gray-600"}`}>
                          {TYPE_LABELS[h.type] || h.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500">{h.region || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <Button type="button" variant="outline" onClick={() => setEditing({ ...h })}
                            className="h-auto px-2.5 py-1 text-xs text-gray-700">Editar</Button>
                          <Button type="button" variant="outline" onClick={() => toggleActive(h)}
                            className="h-auto px-2.5 py-1 text-xs text-gray-700">
                            {h.is_active ? "Desativar" : "Ativar"}</Button>
                          <Button type="button" variant="outline" onClick={() => remove(h)} aria-label="Excluir"
                            className="h-auto px-2.5 py-1 text-xs border-red-200 text-red-600 hover:bg-red-50">
                            <Trash2 size={14} /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ===== Modal novo/editar feriado ===== */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">{editing.id ? "Editar feriado" : "Novo feriado"}</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-600">Data</label>
                <input type="date" className={`${inputCls} w-full`} value={(editing.date || "").slice(0, 10)}
                  onChange={(e) => setEditing({ ...editing, date: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Nome</label>
                <input className={`${inputCls} w-full`} value={editing.name || ""}
                  placeholder="Ex.: Aniversário da empresa"
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Tipo</label>
                <select className={`${inputCls} w-full`} value={editing.type || "custom"}
                  onChange={(e) => setEditing({ ...editing, type: e.target.value })}>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Região (opcional)</label>
                <input className={`${inputCls} w-full`} value={editing.region || ""}
                  placeholder="BR · SP · GO · empresa"
                  onChange={(e) => setEditing({ ...editing, region: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button type="button" onClick={saveHoliday}>Salvar</Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Calendario;
